import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Button, Col, DatePicker, Descriptions, Form, Input, InputNumber, message, Modal,
  Row, Select, Space, Statistic
} from 'antd';
import { CheckCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { investmentsApi } from './investmentsApi';
import {
  findMaturityFormErrors, friendlyErrorFor,
  calculateGainLossPreview, formatRupee
} from './investmentsHelpers';
import '../accounts.css';

// ─────────────────────────────────────────────────────────────────
// Glass-i — Investment Maturity modal (Stage 6B.9E).
//
// Mirror of the Disposal modal — same gain/loss math but stamps the
// investment as `matured` and posts under posting_source='investment_maturity'.
// ─────────────────────────────────────────────────────────────────

function LedgerSelect({ value, onChange, disabled, placeholder }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef();
  const fetcher = useCallback(async (q) => {
    setLoading(true);
    try { return await investmentsApi.ledgerLookup(q || '', 100); }
    finally { setLoading(false); }
  }, []);
  const onOpen = useCallback(async (open) => {
    if (!open || options.length > 0) return;
    try { setOptions(await fetcher('')); } catch (_e) { /* */ }
  }, [options.length, fetcher]);
  const onSearch = useCallback((text) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try { setOptions(await fetcher(text || '')); } catch (_e) { /* */ }
    }, 200);
  }, [fetcher]);
  return (
    <Select
      style={{ width: '100%' }}
      disabled={disabled}
      showSearch
      placeholder={placeholder || 'Search ledger…'}
      value={value || undefined}
      onChange={(v) => onChange(v || null)}
      onDropdownVisibleChange={onOpen}
      onSearch={onSearch}
      filterOption={false}
      loading={loading}
      allowClear
      notFoundContent={loading ? 'Loading…' : 'No ledgers'}
      options={options.map((o) => ({
        value: o.id,
        label: <span>
          <code style={{ marginRight: 6, fontSize: 11, color: '#888' }}>{o.accountCode}</code>
          {o.accountName}
        </span>
      }))}
    />
  );
}

export default function InvestmentMaturityModal({
  open, investment, onClose, onMatured, runWithApproval
}) {
  const [form] = Form.useForm();
  const [maturityValue, setMaturityValue] = useState(0);
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    const defaultDate = investment && investment.maturityDate
      ? dayjs(investment.maturityDate) : dayjs();
    const defaultValue = investment
      ? Number(investment.currentValue) || Number(investment.principalAmount) || 0
      : 0;
    form.setFieldsValue({
      maturityDate: defaultDate,
      maturityValue: defaultValue,
      receivingAccountId: null,
      reason: ''
    });
    setMaturityValue(defaultValue);
  }, [open, form, investment]);

  const preview = useMemo(() => {
    if (!investment) return null;
    return calculateGainLossPreview({
      principalAmount: investment.principalAmount,
      disposalValue: maturityValue
    });
  }, [investment, maturityValue]);

  const runMature = useCallback(async () => {
    if (!investment) return;
    try {
      const values = await form.validateFields();
      const body = {
        maturityDate:       values.maturityDate.format('YYYY-MM-DD'),
        maturityValue:      Number(values.maturityValue) || 0,
        receivingAccountId: values.receivingAccountId || null,
        reason:             values.reason || null
      };
      const errs = findMaturityFormErrors(body);
      if (errs.length) { message.error(errs.join(' ')); return; }
      const confirmed = await new Promise((resolve) => {
        let okClicked = false;
        const gainLossText = preview && preview.gainLossOnDisposal > 0
          ? `gain of ₹ ${formatRupee(preview.gainLossOnDisposal)}`
          : preview && preview.gainLossOnDisposal < 0
            ? `loss of ₹ ${formatRupee(Math.abs(preview.gainLossOnDisposal))}`
            : 'no gain / loss';
        Modal.confirm({
          title: `Mature ${investment.investmentCode}?`,
          icon:  <CheckCircleOutlined style={{ color: '#3f8600' }} />,
          content: `This will post a maturity JV through the engine (${gainLossText}). You will be asked for a 6-digit Approval MFA code to confirm.`,
          okText: 'Mature', cancelText: 'Cancel',
          onOk:    () => { okClicked = true; },
          onCancel: () => { okClicked = false; },
          afterClose: () => resolve(okClicked)
        });
      });
      if (!confirmed) return;
      setPosting(true);
      await runWithApproval({
        operationKey:   'accounts.investment.mature',
        resourceType:   'investment',
        resourceId:     investment.id,
        operationLabel: 'Mature investment',
        resourceLabel:  `${investment.investmentCode} · ${investment.investmentName}`,
        action: (approvalMfaChallengeId) =>
          investmentsApi.mature(investment.id, body, { approvalMfaChallengeId })
      });
      message.success('Investment matured.');
      onMatured && onMatured();
    } catch (e) {
      if (e && e.name === 'ApprovalMfaCancelled') return;
      if (e && e.errorFields) return;
      message.error(friendlyErrorFor(e));
    } finally { setPosting(false); }
  }, [investment, form, preview, runWithApproval, onMatured]);

  if (!investment) return null;

  return (
    <Modal
      title={`Mature ${investment.investmentCode} · ${investment.investmentName}`}
      open={open}
      onCancel={onClose}
      width={760}
      centered
      destroyOnClose
      maskClosable={false}
      footer={
        <Space>
          <Button type="primary" icon={<ClockCircleOutlined />}
            loading={posting} onClick={runMature}>
            Post Maturity
          </Button>
          <Button onClick={onClose}>Close</Button>
        </Space>
      }
      className="erp-accounts-modal"
      styles={{ body: { maxHeight: '74vh', overflowY: 'auto', padding: '12px 16px' } }}
    >
      <Form form={form} layout="vertical" preserve className="erp-accounts-form"
        onValuesChange={(_c, all) => {
          if ('maturityValue' in (_c || {})) {
            setMaturityValue(Number(all.maturityValue) || 0);
          }
        }}>
        <Row gutter={12}>
          <Col xs={24} md={12}>
            <Form.Item label="Maturity Date" name="maturityDate"
              rules={[{ required: true, message: 'Maturity date is required.' }]}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Maturity Value (₹)" name="maturityValue">
              <InputNumber min={0} step={1000} style={{ width: '100%' }}
                formatter={(n) => n ? Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''}
                parser={(s) => Number(String(s || '').replace(/[, ]/g, '')) || 0} />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item label="Receiving Account (required if Maturity Value > 0)" name="receivingAccountId">
          <LedgerSelect placeholder="Bank / Cash ledger" />
        </Form.Item>
        <Form.Item label="Reason / Remarks" name="reason">
          <Input.TextArea rows={2} maxLength={2000}
            placeholder="e.g. FD matured; principal + interest credited to current account" />
        </Form.Item>
      </Form>

      {preview ? (
        <div style={{ marginTop: 12 }}>
          <Row gutter={[10, 10]}>
            <Col xs={12} md={6}>
              <Statistic title="Principal (₹)" value={preview.principal} precision={2} />
            </Col>
            <Col xs={12} md={6}>
              <Statistic title="Maturity Value (₹)" value={preview.proceeds} precision={2} />
            </Col>
            <Col xs={12} md={6}>
              <Statistic title={preview.gainLossOnDisposal >= 0 ? 'Gain (₹)' : 'Loss (₹)'}
                value={Math.abs(preview.gainLossOnDisposal)} precision={2}
                valueStyle={{ color: preview.gainLossOnDisposal >= 0 ? '#3f8600' : '#cf1322' }} />
            </Col>
            <Col xs={12} md={6}>
              <Descriptions size="small" column={1}>
                <Descriptions.Item label="Code">{investment.investmentCode}</Descriptions.Item>
              </Descriptions>
            </Col>
          </Row>
          {!investment.gainLossLedgerAccountId && preview.gainLossOnDisposal !== 0 ? (
            <Alert type="warning" showIcon style={{ marginTop: 10 }}
              message="Gain/Loss ledger not configured on this investment"
              description="Maturity posting requires a Gain/Loss ledger whenever the maturity value differs from the principal amount. Configure it on the investment first." />
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
}
